
import { Stack } from 'ui/stack';
import { Box } from 'ui/box';

import NavButton from 'containers/user/home/components/shared/navigation-button';

import useGoalMonitoringVisibility from 'hooks/use-goal-monitoring-visibility';

import Icons from 'assets/icons';

import type { Goal, CriticalElement } from 'containers/user/goals/use-goals';

interface GoalItemContentProps {
  goal: Goal;
}

const GoalItemContent = ({ goal }: GoalItemContentProps): React.JSX.Element => (
  <Stack sx={{ justifyContent: 'center', alignItems: 'flex-start' }}>
    <Box
      sx={{ fontWeight: 600, mb: 1, textAlign: 'left' }}
      data-testid="goal-item-summary"
    >
      {goal.summary}
    </Box>
  </Stack>
);

interface CriticalElementsListProps {
  elements: CriticalElement[];
}

const CriticalElementsList = ({
  elements,
}: CriticalElementsListProps): React.JSX.Element | null => {
  if (!elements.length) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          fontWeight: 600,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          mb: 1,
        }}
      >
        Critical Elements
      </Box>

      <ul className="list-disc pl-5 text-gray-600">
        {elements.map((element, index) => (
          <li key={index} className="text-sm mb-1">
            {element.summary}
          </li>
        ))}
      </ul>
    </Box>
  );
};

export interface GoalItemProps {
  goal: Goal;
  isMonitoringYear: boolean;
  selectedYear: number;
  hideMonitoringDetails?: boolean;
}

const GoalItem = ({
  goal,
  isMonitoringYear,
  selectedYear,
  hideMonitoringDetails = false,
}: GoalItemProps): React.JSX.Element => {
  const { shouldShowMonitoring, isLoading } = useGoalMonitoringVisibility(
    goal?.criticalElements || [],
    isMonitoringYear && !hideMonitoringDetails
  );

  return (
    <Stack
      sx={[
        {
          bgcolor: 'tokens.navy.50',
          borderRadius: '0.375rem',
          borderWidth: '1px',
          borderColor: 'tokens.navy.100',
        },
        hideMonitoringDetails
          ? { p: 2, width: '328px' }
          : { px: 2.5, py: 2, width: '18rem', minHeight: '7rem' },
      ]}
      data-testid="goal-item"
    >
      {goal.category === 'Other' ? (
        <Stack sx={{ justifyContent: 'center', alignItems: 'flex-start' }}>
          <Box sx={{ fontWeight: 600, textAlign: 'left' }}>{goal.name}</Box>
        </Stack>
      ) : (
        <GoalItemContent goal={goal} />
      )}
      {!hideMonitoringDetails && (
        <CriticalElementsList elements={goal.criticalElements ?? []} />
      )}
      {!hideMonitoringDetails &&
        (isLoading ? (
          <Box
            sx={{
              mt: 1.5,
              display: 'inline-flex',
              alignItems: 'center',
              color: 'tokens.gray.600',
            }}
          >
            <Icons.Spinner
              customClass="w-4 h-4 animate-spin"
              aria-hidden="true"
            />
          </Box>
        ) : shouldShowMonitoring ? (
          <NavButton
            link={`/goals/${selectedYear}/${goal.id}/monitoring`}
            text="Monitoring"
            customClass="mt-3"
          />
        ) : null)}
    </Stack>
  );
};

export default GoalItem;
